import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import {
  createCoComment,
  resolveCoComment,
  unresolveCoComment,
} from "./coReviewApi";
import type { CoReviewComment } from "../../../shared/interfaces/CoReviewComment";
import { useToast } from "../../../shared/toast";
import { coReviewKeys } from "./coReviewQueries";

/**
 * Shared CO-comment mutations with optimistic cache updates. Both observers
 * of the comments cache (CoReviewPage's "all" list, the edit page's
 * "current" list) are patched via the coReviewKeys.comments(reportId)
 * PREFIX, never one exact key, so the lists can't drift.
 *
 * Concurrency contract (the officer "resolve several comments quickly"
 * path): every mutation is scoped to one comment, so
 * - optimistic patches and rollbacks are PER-COMMENT inverse patches, never
 *   whole-list snapshot restores — a failing mutation can only revert its
 *   own comment, not a sibling's confirmed state; and
 * - the reconciling onSettled invalidate runs only on the LAST in-flight
 *   mutation (shared mutationKey + isMutating === 1), because an earlier
 *   refetch would return server state without the still-in-flight siblings
 *   and visibly flip their optimistic patches back.
 * Patches go through setQueriesData updaters, which only touch EXISTING
 * cache entries — a late rollback can never resurrect an entry that
 * gcTime 0 already evicted.
 */

const commentsMutationKey = (reportId: string) =>
  [...coReviewKeys.comments(reportId), "mutation"] as const;

function patchComment(
  queryClient: QueryClient,
  reportId: string,
  commentId: string,
  patch: Partial<CoReviewComment>,
): void {
  queryClient.setQueriesData<CoReviewComment[]>(
    { queryKey: coReviewKeys.comments(reportId) },
    (old) =>
      old?.map((c) => (c.commentId === commentId ? { ...c, ...patch } : c)),
  );
}

function findComment(
  queryClient: QueryClient,
  reportId: string,
  commentId: string,
): CoReviewComment | undefined {
  for (const [, list] of queryClient.getQueriesData<CoReviewComment[]>({
    queryKey: coReviewKeys.comments(reportId),
  })) {
    const hit = list?.find((c) => c.commentId === commentId);
    if (hit) return hit;
  }
  return undefined;
}

function settleInvalidate(queryClient: QueryClient, reportId: string) {
  if (
    queryClient.isMutating({ mutationKey: commentsMutationKey(reportId) }) === 1
  ) {
    return queryClient.invalidateQueries({
      queryKey: coReviewKeys.comments(reportId),
    });
  }
}

function errorMessage(err: unknown, fallback: string): string {
  const e = err as {
    response?: { data?: { error?: string } };
    message?: string;
  };
  return e?.response?.data?.error || e?.message || fallback;
}

export interface ResolveCoCommentVars {
  commentId: string;
  /** Recorded optimistically; the refetch reconciles with server truth. */
  resolvedByUserId: string | null;
}

export function useResolveCoComment(reportId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationKey: commentsMutationKey(reportId),
    mutationFn: ({ commentId }: ResolveCoCommentVars) =>
      resolveCoComment(reportId, commentId),
    onMutate: async ({ commentId, resolvedByUserId }) => {
      await queryClient.cancelQueries({
        queryKey: coReviewKeys.comments(reportId),
      });
      const prior = findComment(queryClient, reportId, commentId);
      patchComment(queryClient, reportId, commentId, {
        resolvedAt: new Date().toISOString(),
        resolvedByUserId,
      });
      return { prior };
    },
    onError: (err, { commentId }, ctx) => {
      patchComment(queryClient, reportId, commentId, {
        resolvedAt: ctx?.prior?.resolvedAt ?? null,
        resolvedByUserId: ctx?.prior?.resolvedByUserId ?? null,
      });
      toast.error(errorMessage(err, "Failed to resolve comment."));
    },
    onSettled: () => settleInvalidate(queryClient, reportId),
  });
}

export function useUnresolveCoComment(reportId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationKey: commentsMutationKey(reportId),
    mutationFn: ({ commentId }: ResolveCoCommentVars) =>
      unresolveCoComment(reportId, commentId),
    onMutate: async ({ commentId }) => {
      await queryClient.cancelQueries({
        queryKey: coReviewKeys.comments(reportId),
      });
      const prior = findComment(queryClient, reportId, commentId);
      patchComment(queryClient, reportId, commentId, {
        resolvedAt: null,
        resolvedByUserId: null,
      });
      return { prior };
    },
    onError: (err, { commentId }, ctx) => {
      patchComment(queryClient, reportId, commentId, {
        resolvedAt: ctx?.prior?.resolvedAt ?? null,
        resolvedByUserId: ctx?.prior?.resolvedByUserId ?? null,
      });
      toast.error(errorMessage(err, "Failed to unresolve comment."));
    },
    onSettled: () => settleInvalidate(queryClient, reportId),
  });
}

export interface ReplyToCoCommentVars {
  /** The parent as rendered — callers keep the parent-visible guard. */
  parent: CoReviewComment;
  text: string;
  author: { userId: string; displayName: string };
}

let optimisticCounter = 0;

export function useReplyToCoComment(reportId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationKey: commentsMutationKey(reportId),
    mutationFn: ({ parent, text, author }: ReplyToCoCommentVars) =>
      createCoComment({
        reportId,
        sectionPath: parent.sectionPath,
        fieldLabel: parent.fieldLabel,
        commentText: text,
        narrativeIndex: parent.narrativeIndex,
        charStart: parent.charStart,
        charEnd: parent.charEnd,
        quotedText: parent.quotedText,
        parentCommentId: parent.commentId,
        authorName: author.displayName,
      }),
    onMutate: async ({ parent, text, author }) => {
      await queryClient.cancelQueries({
        queryKey: coReviewKeys.comments(reportId),
      });
      const optimisticId = `optimistic-reply-${++optimisticCounter}`;
      const optimistic: CoReviewComment = {
        reportId,
        commentId: optimisticId,
        // Replies inherit the parent's cycle (decodeMany does the same on
        // refetch) so the thread renders together immediately.
        reviewCycle: parent.reviewCycle,
        reportVersionNumber: parent.reportVersionNumber,
        sectionPath: parent.sectionPath,
        fieldLabel: parent.fieldLabel,
        authorUserId: author.userId,
        authorName: author.displayName,
        // Parity with decodeServerComment's default — display derives the
        // real role from reportOwnerUserId, not this field.
        authorRole: "CO",
        commentText: text,
        createdAt: new Date().toISOString(),
        resolvedAt: null,
        resolvedByUserId: null,
        parentCommentId: parent.commentId,
        narrativeIndex: parent.narrativeIndex,
        charStart: parent.charStart,
        charEnd: parent.charEnd,
        quotedText: parent.quotedText,
      };
      // Append only to cached lists that actually contain the parent — the
      // "current" list excludes prior-cycle threads, and a reply must not
      // surface a thread the list filtered out.
      queryClient.setQueriesData<CoReviewComment[]>(
        { queryKey: coReviewKeys.comments(reportId) },
        (old) =>
          old?.some((c) => c.commentId === parent.commentId)
            ? [...old, optimistic]
            : old,
      );
      return { optimisticId };
    },
    onSuccess: (created, { parent }, ctx) => {
      // Replace the optimistic row with the server-decoded comment, keeping
      // the server's reportVersionNumber (it feeds new-cycle detection) but
      // inheriting the parent's reviewCycle exactly like decodeMany does on
      // a full refetch.
      const finalized: CoReviewComment = {
        ...created,
        reviewCycle: parent.reviewCycle,
      };
      queryClient.setQueriesData<CoReviewComment[]>(
        { queryKey: coReviewKeys.comments(reportId) },
        (old) =>
          old?.map((c) => (c.commentId === ctx?.optimisticId ? finalized : c)),
      );
    },
    onError: (err, _vars, ctx) => {
      // Remove only this mutation's optimistic row — never a whole-list
      // restore, which would clobber concurrent siblings.
      queryClient.setQueriesData<CoReviewComment[]>(
        { queryKey: coReviewKeys.comments(reportId) },
        (old) => old?.filter((c) => c.commentId !== ctx?.optimisticId),
      );
      toast.error(errorMessage(err, "Failed to post reply."));
    },
    onSettled: () => settleInvalidate(queryClient, reportId),
  });
}
